/**
 * Character creation (M7-03) — ninety seconds, then the world.
 *
 * A signed-in account with no character sees this once: a name, and one of four
 * backgrounds. No point-buy, no attribute sliders — the background *is* the
 * build, and the screen's whole job is to be over quickly and drop the player
 * in the hall. Vanilla DOM like the rest of the client shell (there is no
 * framework here and none planned): the canvas is the game, and this is a form
 * that gets out of the way before the game starts.
 *
 * The background data is `@annwn/content`'s, the same records the server builds
 * the body from — so the card a player reads and the kit they wake up holding
 * cannot drift apart.
 */

import { BACKGROUNDS, type Background } from '@annwn/content';

export interface Creation {
  readonly name: string;
  readonly backgroundId: string;
}

/**
 * A soft Welsh-flavoured suggestion. Not a name authority — free text is always
 * allowed — just three rerollable starting points, because a blank name field
 * is where the ninety seconds goes to die. Syllables kept plain here; the full
 * generator and its UTF-8 audit live with the content package (see the ticket).
 */
const NAME_PARTS = {
  onset: ['Br', 'Gw', 'Ll', 'M', 'R', 'C', 'D', 'T', 'Aer', 'Eil', 'Mor', 'Bl'],
  body: ['an', 'en', 'yn', 'ed', 'ael', 'wen', 'on', 'eir', 'ig', 'aw'],
  tail: ['wy', 'ydd', 'an', 'eth', 'wen', 'ig', 'on', 'ael', 'yr', ''],
} as const;

function suggestName(): string {
  const pick = <T>(items: readonly T[]): T => items[Math.floor(Math.random() * items.length)] as T;
  const raw = `${pick(NAME_PARTS.onset)}${pick(NAME_PARTS.body)}${pick(NAME_PARTS.tail)}`;
  // Never longer than the wire allows (MAX_NAME_LENGTH is 24), and always at
  // least a couple of syllables so it reads as a name and not a typo.
  return raw.slice(0, 16);
}

function threeSuggestions(): string[] {
  const names = new Set<string>();
  let guard = 0;
  while (names.size < 3 && guard < 50) {
    names.add(suggestName());
    guard += 1;
  }
  return [...names];
}

/**
 * Show the creation screen in `root` and resolve with the choice. Replaces the
 * container's contents; the caller builds the game shell afterwards, so there is
 * nothing to tear down here beyond letting the promise settle.
 */
export function runCreation(root: HTMLElement): Promise<Creation> {
  return new Promise<Creation>((resolve) => {
    let selected: Background = BACKGROUNDS[0] as Background;

    root.innerHTML = `
      <div class="creation">
        <div class="creation-panel">
          <h1 class="creation-title">Come to Cwm Twrch</h1>
          <p class="creation-lede">
            Winter is coming to the valley, and it does not care who you were.
            Choose a name, and the life you are bringing with you.
          </p>

          <label class="creation-field">
            <span class="creation-label">Your name</span>
            <input
              id="creation-name"
              class="creation-input"
              type="text"
              maxlength="24"
              autocomplete="off"
              spellcheck="false"
              placeholder="a name for the valley to know you by"
            />
          </label>
          <div class="creation-suggests" id="creation-suggests" aria-label="Name suggestions"></div>

          <fieldset class="creation-backgrounds" id="creation-backgrounds">
            <legend class="creation-label">Where you are from</legend>
          </fieldset>

          <button type="button" class="creation-enter" id="creation-enter" disabled>
            Enter the valley
          </button>
        </div>
      </div>
    `;

    const nameInput = root.querySelector<HTMLInputElement>('#creation-name');
    const suggests = root.querySelector<HTMLDivElement>('#creation-suggests');
    const cards = root.querySelector<HTMLFieldSetElement>('#creation-backgrounds');
    const enter = root.querySelector<HTMLButtonElement>('#creation-enter');
    if (!nameInput || !suggests || !cards || !enter) {
      throw new Error('creation screen failed to build');
    }

    const refreshEnter = (): void => {
      enter.disabled = nameInput.value.trim().length === 0;
    };

    // The name suggestions: three chips, plus a reroll. Clicking one fills the
    // field rather than committing — the player still owns the box.
    const drawSuggestions = (): void => {
      suggests.replaceChildren();
      for (const name of threeSuggestions()) {
        const chip = document.createElement('button');
        chip.type = 'button';
        chip.className = 'creation-chip';
        chip.textContent = name;
        chip.addEventListener('click', () => {
          nameInput.value = name;
          refreshEnter();
          nameInput.focus();
        });
        suggests.append(chip);
      }
      const reroll = document.createElement('button');
      reroll.type = 'button';
      reroll.className = 'creation-chip creation-chip-reroll';
      reroll.textContent = '↻ more';
      reroll.addEventListener('click', drawSuggestions);
      suggests.append(reroll);
    };

    // The four backgrounds, as selectable cards. The first is selected on load
    // so "Enter" is never a dead end for want of a choice.
    const select = (background: Background, card: HTMLElement): void => {
      selected = background;
      for (const other of cards.querySelectorAll('.creation-card')) {
        other.classList.toggle('is-selected', other === card);
        other.setAttribute('aria-pressed', other === card ? 'true' : 'false');
      }
    };

    for (const background of BACKGROUNDS) {
      const card = document.createElement('button');
      card.type = 'button';
      card.className = 'creation-card';
      card.setAttribute('aria-pressed', 'false');

      const title = document.createElement('h2');
      title.className = 'creation-card-name';
      title.textContent = background.nameFeminine
        ? `${background.name} · ${background.nameFeminine}`
        : background.name;

      const blurb = document.createElement('p');
      blurb.className = 'creation-card-blurb';
      blurb.textContent = background.blurb;

      const history = document.createElement('p');
      history.className = 'creation-card-history';
      history.textContent = background.history;

      const familiarity = document.createElement('p');
      familiarity.className = 'creation-card-tag';
      familiarity.textContent = `familiar with ${background.familiarity}`;

      card.append(title, blurb, history, familiarity);
      card.addEventListener('click', () => select(background, card));
      cards.append(card);

      if (background === selected) select(background, card);
    }

    drawSuggestions();
    refreshEnter();
    nameInput.addEventListener('input', refreshEnter);

    const commit = (): void => {
      const name = nameInput.value.trim();
      if (name.length === 0) return;
      resolve({ name, backgroundId: selected.id });
    };

    enter.addEventListener('click', commit);
    nameInput.addEventListener('keydown', (event) => {
      if (event.key === 'Enter') {
        event.preventDefault();
        commit();
      }
    });

    nameInput.focus();
  });
}
